import { Injectable, inject, signal, DestroyRef } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, shareReplay } from 'rxjs';

export const WCAG_BREAKPOINTS = {
	WIDTH_REFLOW: '(max-width: 320px)',
	WIDTH_COMPACT: '(max-width: 600px)', // Intermediate threshold aligned with CDK Breakpoints.XSmall (599.98px)
	HEIGHT_REFLOW: '(max-height: 256px)',
	HEIGHT_COMPACT: '(max-height: 480px)' // Intermediate threshold
} as const;

export const MOBILE_BREAKPOINT = '(max-width: 640px)';

export const RESPONSIVE_CLASSES = {
	WIDTH_REFLOW: 'width-reflow',
	WIDTH_COMPACT: 'width-compact',
	HEIGHT_REFLOW: 'height-reflow',
	HEIGHT_COMPACT: 'height-compact'
} as const;

@Injectable({
	providedIn: 'root'
})
export class ResponsiveLayoutService {
	private readonly destroyRef = inject(DestroyRef);

	/**
	 * CSS classes as a space-separated string, ready to use in templates
	 */
	public readonly cssClassString = signal('');

	public readonly isMobileSize$ = this.breakpointObserver
		.observe(MOBILE_BREAKPOINT)
		.pipe(
			map((state: BreakpointState) => state.matches),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

	constructor (private readonly breakpointObserver: BreakpointObserver) {
		this.breakpointObserver
			.observe(Object.values(WCAG_BREAKPOINTS))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((state: BreakpointState) => this.updateLayoutState(state));
	}

	private updateLayoutState (state: BreakpointState): void {
		const classes: string[] = [];

		if (state.breakpoints[WCAG_BREAKPOINTS.WIDTH_REFLOW]) {
			classes.push(RESPONSIVE_CLASSES.WIDTH_REFLOW);
		}
		if (state.breakpoints[WCAG_BREAKPOINTS.WIDTH_COMPACT]) {
			classes.push(RESPONSIVE_CLASSES.WIDTH_COMPACT);
		}
		if (state.breakpoints[WCAG_BREAKPOINTS.HEIGHT_REFLOW]) {
			classes.push(RESPONSIVE_CLASSES.HEIGHT_REFLOW);
		}
		if (state.breakpoints[WCAG_BREAKPOINTS.HEIGHT_COMPACT]) {
			classes.push(RESPONSIVE_CLASSES.HEIGHT_COMPACT);
		}

		this.cssClassString.set(classes.join(' '));
	}
}
